// =============================================================================
// app/settings/blocked.tsx — blocked-users list + unblock (bare-RN port).
//
// MIGRATION (P3 settings): @expo/vector-icons -> components/ui/Icon; the inline
// expo-router <Stack.Screen> header is dropped (SettingsStack owns it). The
// moderation service (syncBlockedUsers/unblockUser) + API logic are unchanged —
// blocking must persist + take effect everywhere (Apple requirement).
//
// APPLE-STANDARD PASS: flat colors.bg replaced with a full-bleed
// <ParticleBackground/> aurora (absoluteFill, under the SettingsStack header).
// The scattered per-row cards are consolidated into ONE grouped GlassCard with
// hairline-divided rows (matches account.tsx), a section caption for context, a
// header count pill, and typography tightened per skill §15 (weight+size
// hierarchy, negative tracking on the title, body near 0). Unblock stays a
// scoped destructive pill that responds on press (§1). Data/nav/a11y untouched.
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Image,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '../../components/ui/Icon';
import { api } from '../../services/api';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../../constants/theme';
import { GlassCard, GlassButton, glassTokens } from '../../components/glass';
import ParticleBackground from '../../components/ui/ParticleBackground';
import { syncBlockedUsers, unblockUser } from '../../services/moderation';

interface BlockedUser {
  id: string;
  username: string;
  display_name: string;
  avatar_url?: string;
}

export default function BlockedScreen() {
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<BlockedUser[]>([]);

  const fetchBlocked = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Backend returns data: [{ id: blockRecordId, createdAt, user: {...} }].
    // The actual user lives under item.user (camelCase); map to BlockedUser and
    // use the USER's id (not the block-record id) so unblock targets the right
    // user via DELETE /users/:userId/block.
    const res = await api<Array<{ user: { id: string; username: string; displayName?: string; avatar?: string } }>>('/settings/blocked');
    if (res.success && res.data) {
      const list = Array.isArray(res.data) ? res.data : [];
      const mapped = list
        .filter((b) => b && b.user)
        .map((b) => ({
          id: b.user.id,
          username: b.user.username,
          display_name: b.user.displayName || '',
          avatar_url: b.user.avatar,
        }));
      setUsers(mapped);
      // Seed the device block registry from the server so feeds/DMs hide these
      // users immediately (Apple: blocking must persist + take effect everywhere).
      syncBlockedUsers(mapped.map((u) => ({ id: u.id, username: u.username })));
    } else if (!res.success) {
      setError(res.error || 'Failed to load blocked users.');
      setUsers([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchBlocked();
  }, [fetchBlocked]);

  function handleUnblock(user: BlockedUser) {
    Alert.alert(
      'Unblock User',
      `Are you sure you want to unblock @${user.username}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Unblock',
          onPress: async () => {
            // unblockUser also drops them from the device block registry so their
            // content reappears in feeds/DMs.
            const res = await unblockUser(user.id);
            if (res.success) {
              setUsers((prev) => prev.filter((u) => u.id !== user.id));
              Alert.alert('Done', `@${user.username} has been unblocked.`);
            } else {
              Alert.alert('Error', res.error || 'Failed to unblock user.');
            }
          },
        },
      ]
    );
  }

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <View style={styles.emptyWrap}>
          <View style={styles.emptyIcon}>
            <Ionicons name="alert-circle-outline" size={30} color={colors.error} />
          </View>
          <Text style={styles.emptyTitle}>Couldn't load</Text>
          <Text style={styles.emptyDesc}>{error}</Text>
          <GlassButton
            title="Retry"
            onPress={fetchBlocked}
            variant="secondary"
            size="sm"
            style={styles.retryBtn}
          />
        </View>
      </View>
    );
  }

  const count = Array.isArray(users) ? users.length : 0;

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      <FlatList
        data={Array.isArray(users) ? users : []}
        keyExtractor={(item) => item.id}
        contentContainerStyle={[styles.listContent, containerStyle]}
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={
          count > 0 ? (
            <View style={styles.listHeader}>
              <Text style={styles.sectionTitle}>Blocked</Text>
              <View style={styles.countPill}>
                <Text style={styles.countText}>{count}</Text>
              </View>
            </View>
          ) : null
        }
        ListEmptyComponent={
          <View style={styles.emptyWrap}>
            <View style={styles.emptyIcon}>
              <Ionicons name="shield-checkmark-outline" size={30} color={colors.primary} />
            </View>
            <Text style={styles.emptyTitle}>No blocked users</Text>
            <Text style={styles.emptyDesc}>
              Anyone you block will appear here. They won't be able to see your posts or message you.
            </Text>
          </View>
        }
        renderItem={({ item, index }) => {
          const isLast = index === count - 1;
          return (
            <GlassCard padding={0} style={styles.card}>
              <View style={styles.userRow}>
                <View style={styles.userLeft}>
                  {item.avatar_url ? (
                    <Image
                      source={{ uri: item.avatar_url }}
                      style={styles.avatar}
                    />
                  ) : (
                    <View style={[styles.avatar, styles.avatarFallback]}>
                      <Text style={styles.avatarLetter}>
                        {(item.display_name || item.username || 'U')[0].toUpperCase()}
                      </Text>
                    </View>
                  )}
                  <View style={{ flex: 1 }}>
                    <Text style={styles.displayName} numberOfLines={1}>
                      {item.display_name || item.username || 'Unknown user'}
                    </Text>
                    <Text style={styles.username} numberOfLines={1}>
                      @{item.username || 'unknown'}
                    </Text>
                  </View>
                </View>
                <TouchableOpacity
                  style={styles.unblockBtn}
                  onPress={() => handleUnblock(item)}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel={`Unblock ${item.username || 'user'}`}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Text style={styles.unblockText}>Unblock</Text>
                </TouchableOpacity>
              </View>
            </GlassCard>
          );
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    padding: spacing.lg,
    flexGrow: 1,
  },
  listHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.md,
    marginLeft: spacing.xs,
  },
  sectionTitle: {
    ...typographyScale.caption,
    color: colors.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...fonts.bold,
  },
  countPill: {
    minWidth: 22,
    height: 20,
    paddingHorizontal: 7,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary + '18',
  },
  countText: {
    fontSize: 11,
    color: colors.primary,
    ...fonts.bold,
  },
  emptyWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 80,
    paddingHorizontal: spacing.xl,
    gap: spacing.sm,
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: glassTokens.border,
    marginBottom: spacing.xs,
    ...shadowSoft(),
  },
  emptyTitle: {
    ...typographyScale.h3,
    fontSize: 19,
    letterSpacing: -0.3,
    color: colors.ink,
    ...fonts.bold,
  },
  emptyDesc: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
    ...fonts.regular,
  },
  retryBtn: {
    marginTop: spacing.md,
  },
  card: {
    marginBottom: spacing.sm,
  },
  userRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
  },
  userLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    flex: 1,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  avatarFallback: {
    backgroundColor: colors.primary + '20',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarLetter: {
    fontSize: 18,
    color: colors.primary,
    ...fonts.bold,
  },
  displayName: {
    fontSize: 15,
    letterSpacing: -0.1,
    color: colors.ink,
    ...fonts.semibold,
  },
  username: {
    fontSize: 13,
    color: colors.muted,
    ...fonts.regular,
  },
  unblockBtn: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.full,
    borderWidth: 1,
    borderColor: colors.error + '33',
    backgroundColor: colors.error + '0F',
  },
  unblockText: {
    fontSize: 13,
    color: colors.error,
    ...fonts.semibold,
  },
});

// Soft lift for the empty-state icon chip — keeps it from looking flat over the
// aurora without pulling in the heavier card elevation.
function shadowSoft() {
  return {
    shadowColor: 'rgba(108,92,231,0.16)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 12,
    elevation: 3,
  } as const;
}
